/**
 * Maps raw API/Supabase error messages to user-friendly strings.
 *
 * Usage:
 *   catch (err) {
 *     toast({ description: getUserErrorMessage(err) });
 *   }
 *
 *   // Or with a context-specific fallback:
 *   setError(getUserErrorMessage(err, "Failed to save changes."));
 */

/**
 * A refusal by a row-level security policy. Storage says "new row violates row-level security
 * policy" when its rules refuse an upload (supabase-js `upload()` error); PostgREST says the same
 * with "for table …" after it. Neither is a sentence for a user.
 */
const ROW_LEVEL_SECURITY = /row[- ]level security/i;

/**
 * Patterns matched against the error string. First match wins.
 *
 * Status codes are matched as WHOLE numbers (`\b403\b`). Unbounded, `403` matched inside ids —
 * a 400 "Assertion '…208aea403d04' coverage: Share % is required" was shown as a permission
 * refusal (2026-10-02). Hex ids carry these digit runs routinely.
 */
const ERROR_PATTERNS: [test: RegExp, message: string][] = [
  // Auth / permissions. Row-level security comes first: a storage refusal's body also carries
  // `"error": "Unauthorized"`, and it is never an expired session.
  [ROW_LEVEL_SECURITY, "You don't have permission to perform this action."],
  [/unauthorized|\b401\b/i, "Your session has expired. Please sign in again."],
  [/forbidden|\b403\b/i, "You don't have permission to perform this action."],
  [/label.*(not|assign)/i, "Please add a label to your profile first."],

  // Not found
  [/not found|\b404\b/i, "The requested resource was not found."],

  // Network / connectivity
  [/network|fetch|ERR_CONNECTION|ECONNREFUSED/i, "Network error. Please check your connection and try again."],
  [/timeout|timed out|abort/i, "The request timed out. Please try again."],
  [/\b503\b|service unavailable/i, "The service is temporarily unavailable. Please try again shortly."],

  // Database / constraint (from Supabase or backend)
  [/duplicate|unique|already exists|conflict|\b409\b/i, "This item already exists."],
  [/constraint|violation/i, "The operation couldn't be completed due to a data conflict."],
  [/payload too large|\b413\b/i, "The file is too large. Please reduce the file size and try again."],

  // Rate limiting
  [/\b429\b|rate limit|too many/i, "Too many requests. Please wait a moment and try again."],

  // Server errors
  [/\b500\b|internal server/i, "Something went wrong on our end. Please try again."],
];

/**
 * Extract a user-friendly message from any error value.
 *
 * @param error  — Error object, string, or unknown thrown value
 * @param fallback — optional context-specific fallback (e.g. "Failed to publish draft.")
 *                   Used when no pattern matches AND the raw message looks too technical.
 */
export function getUserErrorMessage(
  error: unknown,
  fallback?: string,
): string {
  const raw = extractRawMessage(error);
  if (!raw) return fallback ?? "An unexpected error occurred. Please try again.";

  // Check each pattern
  for (const [pattern, friendly] of ERROR_PATTERNS) {
    if (pattern.test(raw)) return friendly;
  }

  // If the raw message looks technical (contains JSON, status codes, stack traces),
  // use the fallback instead of showing it to the user.
  if (looksLikeTechnicalError(raw)) {
    return fallback ?? "An unexpected error occurred. Please try again.";
  }

  // The message looks human-readable enough — use it as-is.
  return raw;
}

/**
 * Was this refused by a row-level security policy (a storage rule or a table's)? For a caller
 * that can say more than `getUserErrorMessage`'s general "You don't have permission…", such as
 * which rows the user may change.
 */
export function isRowLevelSecurityRefusal(error: unknown): boolean {
  return ROW_LEVEL_SECURITY.test(extractRawMessage(error));
}

/**
 * The machine-readable `reason` of a structured API refusal (`{detail: {message, reason}}`, e.g.
 * 409 `project_archived` or `track_with_seller`), or null. Reads an `ApiError`'s parsed body by
 * shape, so a caller can branch on the reason without matching prose.
 */
export function apiErrorReason(error: unknown): string | null {
  if (!error || typeof error !== "object" || !("body" in error)) return null;
  const detail = (error as { body?: { detail?: unknown } | null }).body?.detail;
  if (!detail || typeof detail !== "object") return null;
  const reason = (detail as { reason?: unknown }).reason;
  return typeof reason === "string" && reason ? reason : null;
}

/** Pull a string out of whatever was thrown. */
function extractRawMessage(error: unknown): string {
  if (!error) return "";
  if (typeof error === "string") return extractApiDetail(error) ?? error;
  if (error instanceof Error) return extractApiDetail(error.message) ?? error.message;
  if (typeof error === "object" && error !== null) {
    const obj = error as Record<string, unknown>;
    if (typeof obj.message === "string") return extractApiDetail(obj.message) ?? obj.message;
    if (typeof obj.detail === "string") return obj.detail;
    if (typeof obj.error === "string") return obj.error;
    try {
      return JSON.stringify(error);
    } catch {
      return String(error);
    }
  }
  return String(error);
}

function extractApiDetail(message: string): string | null {
  if (!/API request failed:/i.test(message)) return null;
  const separatorIndex = message.indexOf(" - ");
  if (separatorIndex === -1) return null;

  try {
    const payload = JSON.parse(message.slice(separatorIndex + 3));
    if (typeof payload?.detail === "string") return payload.detail;
    // A structured refusal (e.g. a 409 `{message, reason, head_entry_id}`) carries its user-facing
    // text in `message`; without this it fell through to the generic "already exists".
    if (typeof payload?.detail?.message === "string") return payload.detail.message;
    // A 422 from FastAPI: `detail` is a list of `{loc, msg, type}`. Without this the user saw
    // "Something went wrong" for a field the API had named precisely (release_air_date, 2026-10-09).
    // Only a validator's own sentence ("Value error, …") is written for people; pydantic's stock
    // texts ("bad uuid", "Input should be a valid integer") are not, so those keep the generic line.
    if (Array.isArray(payload?.detail)) {
      const msgs = payload.detail
        .map((d: { msg?: unknown }) =>
          typeof d?.msg === "string" && d.msg.startsWith("Value error, ") ? d.msg.slice("Value error, ".length) : "",
        )
        .filter(Boolean);
      if (msgs.length) return msgs.join(" ");
    }
  } catch {
    return null;
  }

  return null;
}

/** Heuristic: does this message look too technical for end users? */
function looksLikeTechnicalError(msg: string): boolean {
  return (
    msg.length > 200 ||
    /^\{/.test(msg) ||                     // starts with JSON
    /API request failed:/i.test(msg) ||    // our api-client prefix
    /stack|trace|at \w+\.\w+/i.test(msg) || // stack trace fragments
    /supabase|postgrest|postgres/i.test(msg) ||
    /PGRST|pgcode/i.test(msg)
  );
}
